import React, { useState } from 'react';
import WelcomeModal from '../components/WelcomeModal';

const Section = ({ emoji, title, children }) => (
  <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '1.1rem 1.3rem', marginBottom: '1rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 22 }}>{emoji}</span>
      <h3 style={{ margin: 0, fontSize: 16, color: '#0d2b1a' }}>{title}</h3>
    </div>
    <div style={{ fontSize: 13, color: '#4a4a3a', lineHeight: 1.6 }}>
      {children}
    </div>
  </div>
);

const Code = ({ children }) => (
  <code style={{ background: '#f4f1eb', padding: '1px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>{children}</code>
);

export default function HelpPage() {
  const [showWelcome, setShowWelcome] = useState(false);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.1rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <h2 style={{ fontSize: 22 }}>Help & how-to 🏌️</h2>
          <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>Everything you can do in Greentime, and how.</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => setShowWelcome(true)}>
          Show welcome popup
        </button>
      </div>

      {/* Mission */}
      <div style={{ background: '#f4f1eb', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 14, color: '#0d2b1a', lineHeight: 1.6 }}>
          <strong>Why Greentime exists:</strong> Coordinating tee times across multiple golf groups
          gets messy. Texts get buried, names get mixed up, and you lose track of who said yes.
          Greentime gives you one place to post tee times, see who's actually playing, and stop
          asking "wait, are you in or not?"
        </div>
      </div>

      <Section emoji="📅" title="Posting a tee time">
        <p>Click <strong>+ Post tee time</strong> on Browse. Fill in the course, date, time, and how many spots. Use the autocomplete to pull in details from courses you've used before — saves typing.</p>
        <p>Pick which group(s) to share with. You can post to multiple groups at once. Check <strong>All Friends</strong> to share with everyone in your network (followers, followed, group members, played-with). Check <strong>App-Wide</strong> to make it discoverable to any Greentime user within their radius — useful when you have an open spot and don't mind playing with someone new.</p>
      </Section>

      <Section emoji="📧" title="Email import — the magic shortcut">
        <p>Forward your golf course's confirmation email to <Code>add@mail.greentime.app</Code> and we'll auto-create the tee time. We pull out the date, time, course name, phone number, and cancellation link.</p>
        <p>Forward from the same email address you used to sign up — that's how we know it's you.</p>
        <p style={{ color: '#7a7a6a', fontSize: 12 }}>If we can't read the email, we'll send you a reply with a link to add it manually. Failures get logged so we can keep improving the parser.</p>
      </Section>

      <Section emoji="🌐" title="Groups (and 'All Friends')">
        <p>Head to <strong>My Groups</strong> to create groups for your different golfing circles — Saturday crew, work league, family. Add members by searching for their Greentime account, or invite non-members by email.</p>
        <p>The <strong>All Friends</strong> group is automatic and includes everyone in your friends network — followers, followed users, anyone in your custom groups, and people you've played with. Use it for tee times you're cool sharing broadly.</p>
      </Section>

      <Section emoji="📡" title="App-Wide tee times">
        <p>Check <strong>App-Wide</strong> when posting to open your tee time to any Greentime user nearby — useful when you have an open spot and don't mind playing with someone new. App-Wide is discovery-only: nobody gets a notification, but the tee time shows up in their Browse list if your course is within their radius.</p>
        <p>To see App-Wide tee times from others, set your <strong>home region</strong> in <strong>Profile</strong>. You can also adjust your radius (default 50 miles).</p>
      </Section>

      <Section emoji="👥" title="Joining tee times">
        <p>The <strong>Tee Times</strong> page shows tee times you can join — ones you've been invited to via group, ones shared to All Friends by someone in your network, App-Wide ones near you, or your own. Click <strong>Join</strong> to grab a spot.</p>
        <p>If a tee time is full, you can join the <strong>waitlist</strong>. If a spot opens up, you'll be notified.</p>
        <p>Hosts can also <strong>add a player by name</strong> — useful if your golf buddy isn't on Greentime yet. They'll get an invite email if you provide one.</p>
      </Section>

      <Section emoji="✏️" title="Editing & cancelling">
        <p>Hosts can edit any detail of a tee time after posting. If you change the date or time, joined players get an email with the update.</p>
        <p>When you cancel as a host, we show you the course's phone number and (if it was in your confirmation email) a one-click <strong>Cancel directly with course</strong> button — so you don't forget that step.</p>
      </Section>

      <Section emoji="🕰️" title="Past tee times">
        <p>On <strong>My Schedule</strong>, click <strong>Past</strong> to see tee times you've already played. Filter by Last 90 days, Last year, or All time. Threads on past tee times are read-only — read the banter, but no new posts.</p>
      </Section>

      <Section emoji="📞" title="Phone sharing & friends">
        <p>By default your phone number is private. Toggle <strong>Share with friends</strong> in your <strong>Profile</strong> to share it with anyone in your friends network — they'll see it in the Friends tab.</p>
        <p>Find friends on the <strong>Friends</strong> page. Search by name, follow people, see who you've played with.</p>
      </Section>

      <Section emoji="📅" title="Add to your calendar">
        <p>Every tee time has a 📅 button (small icon on the card, or a button inside the tee time detail). Click it to download an <Code>.ics</Code> file that opens in Apple Calendar, Google Calendar, or Outlook.</p>
        <p>The calendar entry includes the course, time, host, phone number, and reminders for 24 hours before and 1 hour before tee-off.</p>
      </Section>

      <Section emoji="💬" title="Threads & comments">
        <p>Every tee time has its own thread. Joined players (and the host) can chat there — figure out who's driving, settle the bet, post the obligatory hole-in-one brag.</p>
      </Section>

      <Section emoji="❓" title="Anything else">
        <p>Spot a bug or have an idea? Send a thumbs-down on a Claude reply or email <Code>hello@greentime.app</Code>.</p>
      </Section>

      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}
    </div>
  );
}
